import * as d3 from 'd3'
import { MapExtra } from '../../utils/MapExtra'
import { PersonType } from './SDManager'
import colors from 'tailwindcss/colors'
import { CandidateStatusInfo } from '../manageRole/candidateStatusHelper'

export class D3SimulationManager {
    private _sim: d3.Simulation<D3Node, D3Link> | null = null
    private _mainGrp: d3.Selection<
        SVGGElement,
        unknown,
        null,
        undefined
    > | null = null

    private simTickCallbacks = new MapExtra<
        'person' | 'company' | 'link',
        () => void
    >()

    // events
    private readonly onNodeClickListeners: Array<(node: D3Node) => void> = []
    private readonly onNodeHoverListeners: Array<(node: D3Node) => void> = []
    private readonly onNodeUnhoverListeners: Array<(node: D3Node) => void> = []
    private readonly onBackgroundClickListeners: Array<() => void> = []

    // settings
    private personPicSize = 120 // px
    private companyPicSize = 60 // px
    private seedCircleRadius = 180
    private companyCircleRadius = 1000
    private candidateCircleRadius = 1600

    // private props
    private isDragging = false

    // public props
    public nodes: D3Node[] = []
    public links: D3Link[] = []
    // public linkKeys: D3LinkKey[] = []

    public constructor(
        private readonly svg: SVGSVGElement,
        private readonly width = 1000,
        private readonly height = 800,
    ) {
        this.setupSim()
    }

    private get sim(): d3.Simulation<D3Node, D3Link> {
        if (!this._sim) {
            throw new Error('Simulation not set up yet')
        }
        return this._sim
    }

    private get mainGrp(): d3.Selection<SVGGElement, unknown, null, undefined> {
        if (!this._mainGrp) {
            throw new Error('Main group not set up yet')
        }
        return this._mainGrp
    }

    // event listeners
    public registerOnNodeClick(cb: (node: D3Node) => void): void {
        this.onNodeClickListeners.push(cb)
    }
    public registerOnNodeHover(cb: (node: D3Node) => void): void {
        this.onNodeHoverListeners.push(cb)
    }
    public registerOnNodeUnhover(cb: (node: D3Node) => void): void {
        this.onNodeUnhoverListeners.push(cb)
    }

    public registerOnBackgroundClick(cb: () => void): void {
        this.onBackgroundClickListeners.push(cb)
    }

    private calculateAreaRadii(people: number) {
        const basePeople = 3
        const baseRadius = 180
        const rate = 10.48 // Precomputed rate: (400 - 180) / (24 - 3)

        this.seedCircleRadius = baseRadius + (people - basePeople) * rate

        // the grey company circular band is always just 250 extra
        this.companyCircleRadius = this.seedCircleRadius + 250
        this.candidateCircleRadius = this.companyCircleRadius + 500

        // now update the visual circles draw
        d3.select('.seed-circle').attr('r', this.seedCircleRadius)
        d3.select('.company-circle').attr('r', this.companyCircleRadius)
        d3.select('.candidate-circle').attr('r', this.candidateCircleRadius)
    }

    private setupSim() {
        const d3Svg = d3.select(this.svg)

        // Clear any previous content.
        d3Svg.selectAll('*').remove()

        // Append a <defs> element with a clipPath for circular images.
        const defs = d3Svg.append('defs')
        defs.append('clipPath')
            .attr('id', 'clip-circle')
            .attr('clipPathUnits', 'objectBoundingBox')
            .append('circle')
            .attr('cx', 0.5)
            .attr('cy', 0.5)
            .attr('r', 0.5)

        // create a def to allow us to make the profile pics black and white
        defs.append('filter')
            .attr('id', 'gray-scale')
            .append('feColorMatrix')
            .attr('type', 'saturate')
            .attr('values', '0')

        // Append a container group for all diagram elements.
        this._mainGrp = d3Svg.append('g')

        // Create the force simulation.
        this._sim = d3
            .forceSimulation<D3Node>([])
            .force(
                'link',
                d3
                    .forceLink<D3Node, D3Link>([])
                    .id((d) => d.id)
                    .distance((link) => {
                        const sourceNode = link.source as D3Node
                        const targetNode = link.target as D3Node

                        if (
                            (sourceNode.type === 'person' &&
                                targetNode.type === 'company') ||
                            (sourceNode.type === 'person' &&
                                targetNode.type === 'person')
                        ) {
                            // seed person -> seed person OR
                            // seed person -> company
                            //  = shorter and weaker links
                            return 50
                        } else {
                            // company -> candidate
                            //  = longer and stronger links
                            return 100
                        }
                    })
                    .strength((link) => {
                        const sourceNode = link.source as D3Node
                        const targetNode = link.target as D3Node

                        if (
                            (sourceNode.type === 'person' &&
                                targetNode.type === 'company') ||
                            (sourceNode.type === 'person' &&
                                targetNode.type === 'person')
                        ) {
                            // seed person -> company OR
                            // seed person -> seed person
                            //  = shorter and weaker links
                            return 0
                        } else {
                            if (
                                sourceNode.type === 'company' &&
                                targetNode.type === 'person'
                            ) {
                                if (
                                    targetNode.personType ===
                                    PersonType.CANDIDATE
                                ) {
                                    // company -> candidate
                                    //  = longer and stronger links
                                    return 1
                                }
                            }
                        }
                        return 0
                    }),
            )
            .force('charge', d3.forceManyBody().strength(-0.2))
            .force('center', d3.forceCenter(this.width / 2, this.height / 2))
            .force(
                'radial',
                d3
                    .forceRadial(
                        (d: D3Node) => {
                            if (
                                d.type === 'person' &&
                                (d.personType === PersonType.HIRING_MANAGER ||
                                    d.personType === PersonType.NETWORK_SEED)
                            ) {
                                // we want to pull the seed people to the centreish
                                //  and let the collision force stop them from overlapping
                                return this.seedCircleRadius / 2
                            }
                            if (d.type === 'company') {
                                // we want to pull them to the centre of the band - need to work out that radius
                                const radiusDiff =
                                    (this.companyCircleRadius -
                                        this.seedCircleRadius) /
                                    2
                                return this.seedCircleRadius + radiusDiff
                            }
                            if (
                                d.type === 'person' &&
                                d.personType === PersonType.CANDIDATE
                            ) {
                                return this.candidateCircleRadius
                            }

                            throw new Error(
                                `Unknown node type ${d.type} for ${d.id}`,
                            )
                        },
                        this.width / 2,
                        this.height / 2,
                    )
                    .strength(3),
            )
            .force('collide', d3.forceCollide().radius(110).strength(0.5))
        // .force(
        //     'collide',
        //     d3
        //         .forceCollide()
        //         .radius(
        //             Math.sqrt(
        //                 (this.cardWidth / 2) ** 2 +
        //                     (this.cardHeight / 2) ** 2,
        //             ) + this.collisionMargin,
        //         )
        //         .iterations(2)
        //         .strength(0.5),
        // )

        // set up the tick callback - functions can add/overwrite to the module level map to ensure the tick function is calling the latest stuff
        this.sim.on('tick', () => {
            // nodes.forEach((node) => {
            //     // Calculate the distance from the center
            //     const dx = node.x - centerX
            //     const dy = node.y - centerY
            //     let distance = Math.sqrt(dx * dx + dy * dy)

            //     if (node.type === 'trusted') {
            //         // Ensure trusted nodes stay inside the blue circle (radius 250)
            //         if (distance > 250) {
            //             const angle = Math.atan2(dy, dx)
            //             node.x = centerX + 250 * Math.cos(angle)
            //             node.y = centerY + 250 * Math.sin(angle)
            //         }
            //     } else if (node.type === 'organisation') {
            //         // Organisation nodes should remain in the ring: between 250 and 450
            //         if (distance < 250) {
            //             const angle = Math.atan2(dy, dx)
            //             node.x = centerX + 250 * Math.cos(angle)
            //             node.y = centerY + 250 * Math.sin(angle)
            //         } else if (distance > 450) {
            //             const angle = Math.atan2(dy, dx)
            //             node.x = centerX + 450 * Math.cos(angle)
            //             node.y = centerY + 450 * Math.sin(angle)
            //         }
            //     } else if (node.type === 'candidate') {
            //         // Candidate nodes should be outside the gray circle (radius 450)
            //         if (distance < 450) {
            //             const angle = Math.atan2(dy, dx)
            //             node.x = centerX + 450 * Math.cos(angle)
            //             node.y = centerY + 450 * Math.sin(angle)
            //         }
            //     }
            // })

            this.simTickCallbacks.forEach((callback) => callback())
        })

        // set up the background coloured areas for the nodes to live in
        // trusted network area
        this.mainGrp
            .append('circle')
            .attr('class', 'seed-circle')
            .attr('cx', this.width / 2)
            .attr('cy', this.height / 2)
            .attr('r', this.seedCircleRadius)
            .attr('fill', '#0246CE')
            .attr('fill-opacity', 0.1)
            .style('pointer-events', 'none')

        // company area
        this.mainGrp
            .append('circle')
            .attr('class', 'company-circle')
            .attr('cx', this.width / 2)
            .attr('cy', this.height / 2)
            .attr('r', this.companyCircleRadius)
            .attr('fill', 'gray')
            .attr('fill-opacity', 0.1)
            .style('pointer-events', 'none')

        // candidate area - TESTING ONLY
        this.mainGrp
            .append('circle')
            .attr('class', 'candidate-circle')
            .attr('cx', this.width / 2)
            .attr('cy', this.height / 2)
            .attr('r', this.candidateCircleRadius)
            .attr('fill', 'white')
            .attr('fill-opacity', 0.1)
            .style('pointer-events', 'none')

        // --- Zoom and Pan Behavior ---
        // user must be holding alt ("option" on mac) to zoom
        const zoom = d3
            .zoom<SVGSVGElement, unknown>()
            .filter((event) => {
                if (event.type === 'mousedown') return true // Always allow drag to pan
                if (event.type === 'wheel' && event.altKey) return true // Only zoom with Alt + scroll
                if (event.type === 'touchstart') return true // Optional: allow touch
                return false
            })
            .scaleExtent([0.1, 2])
            .on('zoom', (event) => {
                this.mainGrp.attr('transform', event.transform)
            })

        d3Svg.on('dblclick.zoom', null) // Disable default double-click zoom

        // Attach zoom behavior
        d3Svg.call(zoom)

        // Set initial transform (zoomed out and centered)
        const initialScale = 0.5
        const initialTransform = d3.zoomIdentity
            .scale(initialScale)
            .translate(window.innerWidth / 2, window.innerHeight / 2)

        d3Svg.call(zoom.transform, initialTransform)

        // Prevent page scroll when Alt is held and mouse is over the SVG
        // this avoids the issue that if you scroll fully out the mouse wheel suddenly starts scrolling the page
        //  even though the user is still holding the alt key
        d3Svg.node()?.addEventListener(
            'wheel',
            (event) => {
                if (event.altKey) {
                    event.preventDefault()
                }
            },
            { passive: false },
        ) // passive: false is REQUIRED to allow preventDefault
        // END --- Zoom and Pan Behavior ---

        this.createGridBackground()
    }

    private createGridBackground() {
        const gridCellHeight = 100
        const gridCellWidth = 100

        this.mainGrp
            .append('defs')
            .append('pattern')
            .attr('id', 'grid-pattern')
            .attr('width', gridCellWidth) // grid cell width
            .attr('height', gridCellHeight) // grid cell height
            .attr('patternUnits', 'userSpaceOnUse')
            .append('path')
            .attr('d', `M ${gridCellWidth} 0 L 0 0 0 ${gridCellHeight}`)
            .attr('fill', 'none')
            .attr('stroke', '#ccc')
            .attr('stroke-width', 1)
            .attr('stroke-opacity', 0.6) // 0 (invisible) to 1 (fully visible)

        this.mainGrp
            .insert('rect', ':first-child') // insert as the first child of the SVG
            .attr('class', 'grid-background')
            .attr('x', -5000)
            .attr('y', -5000)
            .attr('width', 10000)
            .attr('height', 10000)
            .attr('fill', 'url(#grid-pattern)')

        // the grid is effectively the "white space" now from the user's perspective
        d3.select('.grid-background').on('click', () => {
            for (const cb of this.onBackgroundClickListeners) {
                cb()
            }
        })
    }

    public updatePersonNode(data: UpdatePersonNodeData) {
        // if the status has changed then remove the old status and add the new one
        const currentNode = this.nodes.find((node) => node.id === data.id)

        if (!currentNode || !isPersonNode(currentNode)) {
            throw new Error(`Node with id ${data.id} not found`)
        }

        let removeOldStatusPill = false
        let createNewStatusPill = false

        if (currentNode.statusInfo && !data.statusInfo) {
            // remove the status pill
            removeOldStatusPill = true
        }

        if (!currentNode.statusInfo && data.statusInfo) {
            // create the status pill
            createNewStatusPill = true
        }

        if (currentNode.statusInfo && data.statusInfo) {
            // check for status change
            if (currentNode.statusInfo.status !== data.statusInfo.status) {
                // remove the old status pill
                // create the new status pill
                removeOldStatusPill = true
                createNewStatusPill = true
            }
        }

        // update our local node data - it's important to do this here so the callbacks
        //  are always working with the latest data e.g. the "d" param in the callbacks
        currentNode.statusInfo = data.statusInfo

        if (removeOldStatusPill) {
            const statusPill = d3
                .select(`g.node-${data.id}`)
                .select('g.status-pill')
            statusPill.remove()
        }

        if (createNewStatusPill) {
            const singleNodeGroup = d3
                .select<SVGGElement, D3PersonNode>(`g.node-${data.id}`)
                .select<SVGGElement>('g.node-main')
            this.drawPersonNodeStatusPill(singleNodeGroup)
        }
    }

    public updateData(
        newNodeData: D3Node[],
        newLinkData: D3Link[],
        isFirstUpdate: boolean = false,
    ) {
        this.nodes = newNodeData
        this.links = newLinkData
        // this.linkKeys = newLinkData.map((link) => ({
        //     sourceId:
        //         typeof link.source === 'string' ? link.source : link.source.id,
        //     targetId:
        //         typeof link.target === 'string' ? link.target : link.target.id,
        // }))

        // work out the new circular areas for different types of nodes
        let seedPersonCount = 0
        for (const node of this.nodes) {
            if (
                node.type === 'person' &&
                (node.personType === PersonType.HIRING_MANAGER ||
                    node.personType === PersonType.NETWORK_SEED)
            ) {
                seedPersonCount++
            }
        }
        this.calculateAreaRadii(seedPersonCount)

        // Update the simulation.
        this.sim.nodes(newNodeData)
        const linkForce = this.sim.force('link') as d3.ForceLink<D3Node, D3Link>
        linkForce.links(newLinkData)

        // draw links first to ensure they visually sit under the nodes
        this.drawLinks(newLinkData)
        this.drawPersonNodes(newNodeData.filter(isPersonNode))
        this.drawCompanyNodes(newNodeData.filter(isCompanyNode))

        if (isFirstUpdate) {
            // Heat the simulation.
            this.sim.alpha(1).restart()
        } else {
            // Reheat the simulation.
            this.sim.alpha(0.5).restart()
        }
    }

    private drawPersonNodes(nodes: D3PersonNode[]) {
        // Define layout constants for the card.
        const cardPadding = 10
        // Assume imageSize and radius are defined elsewhere (e.g. imageSize = 60, radius = imageSize/2)
        const cardWidth = this.personPicSize + cardPadding * 2
        // Reserve space for the image and the name (20px extra for the name label)
        const cardHeight = this.personPicSize + cardPadding + 70
        // Compute where the image should be placed inside the card.
        const imageX = -(this.personPicSize / 2) // Center the image horizontally
        const imageY = -64

        // Create (or select) container for node groups.
        let nodeContainer = this.mainGrp.select<SVGGElement>('g.nodes')
        if (nodeContainer.empty()) {
            nodeContainer = this.mainGrp
                .append('g')
                .attr('class', 'nodes')
                .attr('cursor', 'pointer')
        }

        // Data join on the node groups.
        const nodeSelection = nodeContainer
            .selectAll<SVGGElement, D3Node>('g.node')
            .data(nodes, (d: D3Node) => d.id)

        nodeSelection.exit().remove()

        // allow the callbacks to access the simulation object
        const simulation = this.sim

        // Create new nodes.
        const nodeEnter = nodeSelection
            .enter()
            .append('g')
            .attr('class', (d) => `node node-${d.id}`)
            .call(
                d3
                    .drag<SVGGElement, D3PersonNode>()
                    .on('start', (event, d) => {
                        this.isDragging = true
                        d3.select(event.currentTarget).attr(
                            'cursor',
                            'grabbing',
                        )
                        if (!event.active)
                            simulation.alphaTarget(0.01).restart()
                        d.fx = d.x
                        d.fy = d.y
                    })
                    .on('drag', function (event, d) {
                        d.fx = event.x
                        d.fy = event.y
                    })
                    .on('end', (event, d) => {
                        this.isDragging = false
                        d3.select(event.currentTarget).attr('cursor', 'pointer')
                        if (!event.active) simulation.alphaTarget(0)
                        // Clear fixed positions to allow simulation forces to act naturally
                        d.fx = null
                        d.fy = null
                    }),
            )
            .on('mouseover', (_, d) => {
                // Increase the node's card border thickness.
                // d3.select(this).select('rect.card-bg').attr('stroke-width', 3)
                // // Increase the stroke width for all links connected to this node.
                // d3.selectAll('line.link')
                //     .filter(
                //         (linkData: any) =>
                //             linkData.source.id === d.id ||
                //             linkData.target.id === d.id,
                //     )
                //     .attr('stroke-width', 5)

                // if we're dragging we don't want to fire the hover events on any person nodes
                if (this.isDragging) return
                for (const cb of this.onNodeHoverListeners) {
                    cb(d)
                }
            })
            .on('mouseout', (_, d) => {
                // Revert the node's card border thickness.
                // d3.select(this).select('rect.card-bg').attr('stroke-width', 1)
                // // Revert the stroke width for all connected links.
                // d3.selectAll('line.link')
                //     .filter(
                //         (linkData: any) =>
                //             linkData.source.id === d.id ||
                //             linkData.target.id === d.id,
                //     )
                //     .attr('stroke-width', 2)

                if (this.isDragging) return
                for (const cb of this.onNodeUnhoverListeners) {
                    cb(d)
                }
            })
            .on('click', (_, d) => {
                for (const cb of this.onNodeClickListeners) {
                    cb(d)
                }
            })

        // this is a solid white background for the card
        //  when we make them opaque we don't make this opaque
        //  which will avoid the lines showing through the card
        nodeEnter
            .append('rect')
            .attr('class', 'card-bg-white')
            .attr('x', -cardWidth / 2)
            .attr('y', -cardHeight / 2)
            .attr('width', cardWidth)
            .attr('height', cardHeight)
            .attr('rx', 8) // rounded corners
            .attr('ry', 8)
            .attr('fill', 'white')

        // create a group for the content of the card to live in
        //  we can make this group opaque to make the card opaque
        const nodeMainGrp = nodeEnter.append('g').attr('class', 'node-main')

        // Append the card background (a rounded rectangle) so the node’s “hit area” is the card.
        nodeMainGrp
            .append('rect')
            .attr('class', 'card-bg')
            .attr('x', -cardWidth / 2)
            .attr('y', -cardHeight / 2)
            .attr('width', cardWidth)
            .attr('height', cardHeight)
            .attr('rx', 8) // rounded corners
            .attr('ry', 8)
            .attr('fill', 'white')
            .attr('stroke', colors.gray[400])

        // Append the circular profile image.
        nodeMainGrp
            .append('image')
            .attr('class', 'profile-pic')
            .attr('href', (d) => d.picUrl)
            .attr('width', this.personPicSize)
            .attr('height', this.personPicSize)
            .attr('x', imageX)
            .attr('y', imageY)
            .attr('clip-path', 'url(#clip-circle)')
            .each(function () {
                this.addEventListener('error', function () {
                    this.setAttribute('href', '/no-profile-pic.png')
                })
            })

        // Append the medal icon (if the node has one) in the card’s top-right.
        nodeMainGrp
            .filter((d) => d.personType === PersonType.HIRING_MANAGER)
            .append('text')
            .attr('class', 'medal-icon')
            // Top right: card’s right edge is at cardWidth/2; adjust by cardPadding and a small offset.
            .attr('x', cardWidth / 2 - cardPadding - 15)
            .attr('y', -cardHeight / 2 + cardPadding + 15)
            .attr('font-family', '"Font Awesome 5 Free"')
            .attr('font-weight', '900')
            .attr('font-size', '20px')
            .attr('fill', () => '#0246CE')
            .text('\uf005')
            .append('title')
            .text(() => 'Hiring Team')

        // Append the job relevancy pill (if applicable) in the card’s top-left.
        const jobPillEnter = nodeMainGrp
            .filter((d) => !!d.jobRelevancy)
            .append('g')
            .attr('class', 'job-pill')
            // Top left: card’s left edge is at -cardWidth/2; adjust by cardPadding.
            .attr(
                'transform',
                `translate(${-cardWidth / 2 + cardPadding - 1}, ${
                    -cardHeight / 2 + cardPadding + 10
                })`,
            )

        jobPillEnter
            .append('text')
            .attr('font-size', '14px')
            .attr('fill', 'white')
            .text((d) =>
                d.jobRelevancy === 'high'
                    ? 'High match'
                    : d.jobRelevancy === 'medium'
                    ? 'Medium match'
                    : 'Low',
            )
            .each(function (d) {
                const bBox = (this as SVGTextElement).getBBox()
                d3.select<SVGGElement, Node>(this.parentNode as SVGGElement)
                    .insert('rect', 'text')
                    .attr('x', bBox.x - 2)
                    .attr('y', bBox.y - 2)
                    .attr('width', bBox.width + 4)
                    .attr('height', bBox.height + 4)
                    .attr(
                        'fill',
                        d.jobRelevancy === 'high' ? '#2ECC71' : '#F39C12',
                    )
                    .attr('rx', 4)
                    .attr('ry', 4)
            })

        // Append the name text inside the card below the image.
        const nameFontSizePx = 20
        const firstNameY = 74
        nodeMainGrp
            .append('text')
            .attr('class', 'node-name')
            .attr('text-anchor', 'middle')
            .style('font-size', `${nameFontSizePx}px`)
            .attr('x', 0)
            // Place the text near the bottom of the card (a small margin from the bottom edge).
            .attr('y', firstNameY)
            .text((d) => d.firstName)

        nodeMainGrp
            .append('text')
            .attr('class', 'node-name')
            .attr('text-anchor', 'middle')
            .style('font-size', `${nameFontSizePx}px`)
            .attr('x', 0)
            // Place the text near the bottom of the card (a small margin from the bottom edge).
            .attr('y', firstNameY + nameFontSizePx)
            .text((d) => d.lastName)

        // Append the status pill
        this.drawPersonNodeStatusPill(nodeMainGrp)
        // const statusPillEnter = nodeMainGrp
        //     .filter((d) => !!d.statusInfo)
        //     .append('g')
        //     .attr('class', 'status-pill')

        // statusPillEnter
        //     .append('text')
        //     .attr('class', 'pill-text')
        //     .attr('font-size', '16px')
        //     .attr('fill', 'white')
        //     .text((d) => {
        //         return d.statusInfo?.statusLabel ?? 'Unknown'
        //     })
        //     .attr('x', -55)
        //     .attr('y', -76)

        // statusPillEnter.each(function () {
        //     const group = d3.select<SVGGElement, D3PersonNode>(this)
        //     const textSelection = group.select('text')
        //     const textNode = textSelection.node() as SVGTextElement | null

        //     if (!textNode) {
        //         throw new Error('Text node not found')
        //     }
        //     const bBox = textNode.getBBox()
        //     const paddingX = 6
        //     const paddingY = 2

        //     group
        //         .insert('rect', 'text')
        //         .attr('x', bBox.x - paddingX)
        //         .attr('y', bBox.y - paddingY)
        //         .attr('width', bBox.width + paddingX * 2)
        //         .attr('height', bBox.height + paddingY * 2)
        //         .attr('rx', 4)
        //         .attr('ry', 4)
        //         .attr('fill', (d) => {
        //             return d.statusInfo?.colourHex ?? '#000000'
        //         })
        //         .style('opacity', '0.7')
        // })

        // Merge the new nodes with existing ones.
        const nodeMerged = nodeEnter.merge(nodeMainGrp as any)

        const tickCb = () => {
            nodeMerged.attr(
                'transform',
                (d) => `translate(${d.x ?? 0}, ${d.y ?? 0})`,
            )
        }

        // overwrite any previous tick callback to ensure it has access to all the latest nodes
        this.simTickCallbacks.set('person', tickCb)
    }

    private drawPersonNodeStatusPill(
        selection: d3.Selection<SVGGElement, D3PersonNode, any, any>,
    ) {
        const statusPillEnter = selection
            .filter((d) => !!d.statusInfo)
            .append('g')
            .attr('class', 'status-pill')

        statusPillEnter
            .append('text')
            .attr('class', 'pill-text')
            .attr('font-size', '16px')
            .attr('fill', 'white')
            .text((d) => {
                return d.statusInfo?.statusLabel ?? 'Unknown'
            })
            .attr('x', -55)
            .attr('y', -76)

        statusPillEnter.each(function () {
            const group = d3.select<SVGGElement, D3PersonNode>(this)
            const textSelection = group.select('text')
            const textNode = textSelection.node() as SVGTextElement | null

            if (!textNode) {
                throw new Error('Text node not found')
            }
            const bBox = textNode.getBBox()
            const paddingX = 6
            const paddingY = 2

            group
                .insert('rect', 'text')
                .attr('x', bBox.x - paddingX)
                .attr('y', bBox.y - paddingY)
                .attr('width', bBox.width + paddingX * 2)
                .attr('height', bBox.height + paddingY * 2)
                .attr('rx', 4)
                .attr('ry', 4)
                .attr('fill', (d) => {
                    return d.statusInfo?.colourHex ?? '#000000'
                })
                .style('opacity', '1')
        })
    }

    private drawCompanyNodes(nodes: D3CompanyNode[]) {
        // Select or create the container for company nodes
        let nodeContainer = this.mainGrp.select<SVGGElement>('g.company-nodes')
        if (nodeContainer.empty()) {
            nodeContainer = this.mainGrp
                .append('g')
                .attr('class', 'company-nodes')
                .attr('cursor', 'pointer')
        }

        // Data join for company nodes using the proper selector
        const nodeSelection = nodeContainer
            .selectAll<SVGGElement, D3CompanyNode>('g.company-node')
            .data(nodes, (d: D3CompanyNode) => d.id)

        // Remove any nodes that no longer exist
        nodeSelection.exit().remove()

        // ENTER: Create new company node groups
        const nodeEnter = nodeSelection
            .enter()
            .append('g')
            .attr('class', 'company-node')
            .call(
                d3
                    .drag<SVGGElement, D3CompanyNode>()
                    .on('start', (event, d) => {
                        this.isDragging = true
                        d3.select(event.currentTarget).attr(
                            'cursor',
                            'grabbing',
                        )
                        if (!event.active) this.sim.alphaTarget(0.05).restart()
                        d.fx = d.x
                        d.fy = d.y
                    })
                    .on('drag', function (event, d) {
                        d.fx = event.x
                        d.fy = event.y
                    })
                    .on('end', (event, d) => {
                        this.isDragging = false
                        d3.select(event.currentTarget).attr('cursor', 'pointer')
                        if (!event.active) this.sim.alphaTarget(0)
                        d.fx = null
                        d.fy = null
                    }),
            )
            .on('mouseover', (_, d) => {
                if (this.isDragging) return
                for (const cb of this.onNodeHoverListeners) {
                    cb(d)
                }
            })
            .on('mouseout', (_, d) => {
                if (this.isDragging) return
                for (const cb of this.onNodeUnhoverListeners) {
                    cb(d)
                }
            })
            .on('click', (_, d) => {
                for (const cb of this.onNodeClickListeners) {
                    cb(d)
                }
            })

        // Append a rectangle as the background for the node.
        // (This rectangle can be styled/removed via CSS when disabled/hidden.)
        nodeEnter
            .append('rect')
            .attr('class', 'company-rect')
            .attr('x', -this.companyPicSize / 2)
            .attr('y', -this.companyPicSize / 2)
            .attr('width', this.companyPicSize)
            .attr('height', this.companyPicSize)
            .attr('fill', 'white')

        // Append the company logo image (square logo)
        nodeEnter
            .append('image')
            .attr('class', 'company-logo')
            .attr('href', () => 'no-company-logo.png')
            .attr('width', this.companyPicSize)
            .attr('height', this.companyPicSize)
            .attr('x', -this.companyPicSize / 2)
            .attr('y', -this.companyPicSize / 2)

        // Append the company name as a text label
        const companyLabelFontSizePx = 30
        nodeEnter
            .append('text')
            .attr('class', 'company-label')
            .attr('text-anchor', 'middle')
            .attr('dy', `${this.companyPicSize / 2 + companyLabelFontSizePx}px`)
            .style('font-size', `${companyLabelFontSizePx}px`)
            .text((d) => d.name)

        // Merge enter and update selections so all nodes have the same structure
        const nodeMerge = nodeEnter.merge(nodeSelection as any)

        // Tick callback: update the transform for both the node elements and their labels together
        const tickCb = () => {
            nodeMerge.attr(
                'transform',
                (d) => `translate(${d.x ?? 0}, ${d.y ?? 0})`,
            )
        }

        this.simTickCallbacks.set('company', tickCb)
    }

    private drawLinks(links: D3Link[]) {
        // KEY FUNCTION: create a unique key for each link using source and target IDs.
        const keyFn = (d: D3Link) => {
            const src = typeof d.source === 'string' ? d.source : d.source.id
            const tgt = typeof d.target === 'string' ? d.target : d.target.id
            return src + '-' + tgt
        }

        // --- Links ---
        // Select or create the links container group
        let linksContainer = this.mainGrp.select<SVGGElement>('g.links')
        if (linksContainer.empty()) {
            linksContainer = this.mainGrp.append('g').attr('class', 'links')
        }
        // Data join for <line> elements
        const linkSelection = linksContainer
            .selectAll<SVGLineElement, D3Link>('line')
            .data(links, keyFn)

        // Remove any links that no longer exist
        linkSelection.exit().remove()

        // Append new links for the enter selection
        const linkEnter = linkSelection
            .enter()
            .append('line')
            .attr('class', 'link') // Add a class for easier selection
            .attr('stroke', (d) => d.colour)
            .attr('stroke-opacity', 0.8)
            .attr('stroke-width', 2)

        // Merge the new links with the existing ones
        const linksGrp = linkEnter.merge(linkSelection)

        const tickCb = () => {
            linksGrp
                .attr('x1', (d) => (d.source as D3Node).x ?? 0)
                .attr('y1', (d) => (d.source as D3Node).y ?? 0)
                .attr('x2', (d) => (d.target as D3Node).x ?? 0)
                .attr('y2', (d) => (d.target as D3Node).y ?? 0)
        }

        // overwrite any previous tick callback to ensure it has access to all the latest nodes
        this.simTickCallbacks.set('link', tickCb)
    }

    // UI update functions

    // nodes
    // public disablePersonNode(id: string) {
    //     const mainNodes = d3
    //         .selectAll<SVGGElement, D3PersonNode>('g.node-main')
    //         .filter((d) => d.id === id)
    //         .attr('opacity', 0.3)

    //     mainNodes.select('.profile-pic').attr('filter', 'url(#gray-scale)')
    // }

    public highlightPersonNodes(ids: string[]) {
        d3.selectAll<SVGRectElement, D3PersonNode>('rect.card-bg')
            .filter((d) => ids.includes(d.id))
            .attr('stroke-width', 4)
    }

    public unhighlightPersonNodes(ids: string[]) {
        d3.selectAll<SVGRectElement, D3PersonNode>('rect.card-bg')
            .filter((d) => ids.includes(d.id))
            .attr('stroke-width', 1)
    }

    public disablePersonNodes(ids: string[]) {
        const mainNodes = d3
            .selectAll<SVGGElement, D3PersonNode>('g.node-main')
            .filter((d) => ids.includes(d.id))
            .attr('opacity', 0.2)

        mainNodes.select('.profile-pic').attr('filter', 'url(#gray-scale)')
    }

    public enablePersonNodes(ids: string[]) {
        const mainNodes = d3
            .selectAll<SVGGElement, D3PersonNode>('g.node-main')
            .filter((d) => ids.includes(d.id))
            .attr('opacity', 1)

        mainNodes.select('.profile-pic').attr('filter', null)
    }

    public enableAllPersonNodes() {
        const mainNodes = d3
            .selectAll<SVGGElement, D3PersonNode>('g.node-main')
            .attr('opacity', 1)

        mainNodes.select('.profile-pic').attr('filter', null)
    }

    public hidePersonNodes(ids: string[]) {
        d3.selectAll<SVGGElement, D3PersonNode>('g.node')
            .filter((d) => ids.includes(d.id))
            .attr('display', 'none')
    }

    public showPersonNodes(ids: string[]) {
        d3.selectAll<SVGGElement, D3PersonNode>('g.node')
            .filter((d) => ids.includes(d.id))
            .attr('display', null)
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public highlightCompanyNodes(_ids: string[]) {
        // console.log(ids)
        // d3.selectAll<SVGRectElement, D3PersonNode>('rect.card-bg')
        //     .filter((d) => ids.includes(d.id))
        //     .attr('stroke-width', 2)
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public unhighlightCompanyNodes(_ids: string[]) {
        // console.log(ids)
        // d3.selectAll<SVGRectElement, D3PersonNode>('rect.card-bg')
        //     .filter((d) => ids.includes(d.id))
        //     .attr('stroke-width', 1)
    }

    public disableCompanyNodes(ids: string[]) {
        d3.selectAll<SVGGElement, D3CompanyNode>('g.company-node')
            .filter((d) => ids.includes(d.id))
            .attr('opacity', 0.2)
    }

    public enableCompanyNodes(ids: string[]) {
        d3.selectAll<SVGGElement, D3CompanyNode>('g.company-node')
            .filter((d) => ids.includes(d.id))
            .attr('opacity', 1)
    }

    public hideCompanyNodes(ids: string[]) {
        d3.selectAll<SVGGElement, D3CompanyNode>('g.company-node')
            .filter((d) => ids.includes(d.id))
            .attr('display', 'none')
    }

    public showCompanyNodes(ids: string[]) {
        d3.selectAll<SVGGElement, D3CompanyNode>('g.company-node')
            .filter((d) => ids.includes(d.id))
            .attr('display', null)
    }

    // links
    public enableLinks(linkKeys: string[]) {
        const linkKeySet = new Set(linkKeys)
        d3.selectAll<SVGLineElement, D3Link>('line.link')
            .filter((d) => linkKeySet.has(getLinkKey(d.source, d.target)))
            .attr('stroke-opacity', 0.8)
            // put the link back to its original colour
            .attr('stroke', (d) => d.colour)
    }

    public disableLinks(linkKeys: string[]) {
        const linkKeySet = new Set(linkKeys)
        d3.selectAll<SVGLineElement, D3Link>('line.link')
            .filter((d) => linkKeySet.has(getLinkKey(d.source, d.target)))
            .attr('stroke-opacity', 0.2)
            .attr('stroke', 'gray')
    }

    public highlightLinks(linkKeys: string[]) {
        const linkKeySet = new Set(linkKeys)
        const selectedLinks = d3
            .selectAll<SVGLineElement, D3Link>('line.link')
            .filter((d) => linkKeySet.has(getLinkKey(d.source, d.target)))

        selectedLinks
            // .raise() // bring to front
            .attr('stroke-width', 5)
    }

    public unhighlightLinks(linkKeys: string[]) {
        const linkKeySet = new Set(linkKeys)
        d3.selectAll<SVGLineElement, D3Link>('line.link')
            .filter((d) => linkKeySet.has(getLinkKey(d.source, d.target)))
            .attr('stroke-width', 2)
    }

    public showLinks(linkKeys: string[]) {
        const linkKeySet = new Set(linkKeys)
        d3.selectAll<SVGLineElement, D3Link>('line.link')
            .filter((d) => linkKeySet.has(getLinkKey(d.source, d.target)))
            .attr('display', null)
    }

    public hideLinks(linkKeys: string[]) {
        const linkKeySet = new Set(linkKeys)
        d3.selectAll<SVGLineElement, D3Link>('line.link')
            .filter((d) => linkKeySet.has(getLinkKey(d.source, d.target)))
            .attr('display', 'none')
    }

    // public updateLinkThickness(
    //     linkSourceId: string,
    //     linkTargetId: string,
    //     thickness: number,
    // ) {
    //     d3.selectAll<SVGLineElement, D3Link>('line.link')
    //         .filter((linkData) => {
    //             const sourceId =
    //                 typeof linkData.source === 'string'
    //                     ? linkData.source
    //                     : linkData.source.id
    //             const targetId =
    //                 typeof linkData.target === 'string'
    //                     ? linkData.target
    //                     : linkData.target.id

    //             return sourceId === linkSourceId && targetId === linkTargetId
    //         })
    //         .attr('stroke-width', thickness)
    // }

    // public updateLinkThicknessAll(thickness: number) {
    //     d3.selectAll<SVGLineElement, D3Link>('line.link').attr(
    //         'stroke-width',
    //         thickness,
    //     )
    // }
}

export type D3Node = D3PersonNode | D3CompanyNode

export function isPersonNode(node: D3Node): node is D3PersonNode {
    return node.type === 'person'
}

export function isCompanyNode(node: D3Node): node is D3CompanyNode {
    return node.type === 'company'
}

export interface D3PersonNode extends d3.SimulationNodeDatum {
    id: string
    type: 'person'
    firstName: string
    lastName: string
    picUrl: string
    personType: PersonType
    jobRelevancy?: 'high' | 'medium'
    outlineColour?: string
    statusInfo?: CandidateStatusInfo
}

export interface D3CompanyNode extends d3.SimulationNodeDatum {
    id: string
    type: 'company'
    name: string
}

export interface D3Link extends d3.SimulationLinkDatum<D3Node> {
    source: string | D3Node
    target: string | D3Node
    // isMainParent: boolean
    colour: '#0246CE' | '#2ECC71' | 'grey' | string // #0246CE blue #2ECC71 green
}

export function getLinkKey(
    sourceId: string | D3Node,
    targetId: string | D3Node,
): string {
    const sourceIdStr: string =
        typeof sourceId === 'string' ? sourceId : sourceId.id
    const targetIdStr: string =
        typeof targetId === 'string' ? targetId : targetId.id

    return `${sourceIdStr}-${targetIdStr}`
}

export type UpdatePersonNodeData = {
    id: string
    statusInfo?: CandidateStatusInfo
}

// export class D3LinkKey {
//     public constructor(
//         public readonly sourceId: string,
//         public readonly targetId: string,
//     ) {}

//     public toString() {
//         return `${this.sourceId}-${this.targetId}`
//     }
// }
